import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { Glyphicon, Button } from '@bingads-webui-react/primitive';
import { Button as FluentButton } from '@fluentui/react-components';
import { DismissCircle20Regular } from '@fluentui/react-icons';

import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import type { DefaultStyleClasses } from '@bingads-webui-react/with-default-styles';
import { submitEvent } from '@bingads-webui-universal/accessibility-helper';
import { withStateStore } from '@bingads-webui/with-state-store';
import { UXRefreshToggleContext } from '@bingads-webui-react/preference-utility';
import { isCopilotRegionRestricted, isCopilotFraudRestricted } from '@bingads-webui-react/copilot-utils';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import type { CopilotPermissions } from '@bingads-webui-react/copilot-utils';
import { styles, stylesNew } from './styles';

import { notificationStore } from '../notification-store';
import {
  INotification,
  INotificationAction,
  NotificationI18n,
  TopBannerMessageWrapperProps,
  TopBannerNotification,
} from '../types';
import { INotificationLogger, ClickType, DismissTrigger } from '@bingads-webui-react/notification-telemetry';
import { ButtonWrapper as DefaultButtonWrapper } from './button-wrapper';

const COPILOT_OR_HELP_CALLBACK = 'copilot-or-help';
const HELP_TOPIC_CLASS = 'HelpTopic';

type InteractiveGlyphiconProps = React.ComponentProps<typeof Glyphicon> & React.HTMLAttributes<HTMLSpanElement>;
const InteractiveGlyphicon = Glyphicon as React.ComponentType<InteractiveGlyphiconProps>;

export interface ITopBannerProps {
  notifications: TopBannerNotification[];
  i18n: NotificationI18n;
  classes: DefaultStyleClasses;
  Wrapper?: React.ComponentType<TopBannerMessageWrapperProps>; // this is for warpping part of the message as button or callout
  telemetryLogger?: INotificationLogger;
  notificationDataList?: INotification[];
  serverContext?: unknown;
}

export function UnstyledTopBanner(props: ITopBannerProps) {
  const {
    notifications, i18n, Wrapper, classes, telemetryLogger, notificationDataList,
    serverContext,
  } = props;
  const {
    isUXRefreshEnabled,
  } = useContext(UXRefreshToggleContext);
  const permissions = useContext(PermissionsContext);
  const copilotPermissions = permissions as CopilotPermissions;
  const [curIndex, setCurIndex] = useState(0);
  const [isLiveAgentOnlyMode, setIsLiveAgentOnlyMode] = useState(false);
  const hasCopilotOrHelpAction = notifications.some(n =>
    n.actionList?.some(a => a.callbackType === COPILOT_OR_HELP_CALLBACK));
  // Generate stable render instance ID (same pattern as NotificationTelemetryBridge.newRenderInstanceId)
  const renderInstanceIdRef = useRef(`${Date.now()}-${Math.random().toString(36).substring(2, 8)}`);

  const safeIndex = curIndex >= notifications.length ? 0 : curIndex;
  const getNotificationData = (index: number) => notificationDataList && notificationDataList[index];

  // Helper to reduce repeated telemetry guard pattern
  const withTelemetry = (fn: (logger: INotificationLogger, data: INotification) => void) => {
    if (!telemetryLogger) return;
    const data = getNotificationData(safeIndex);
    if (data) fn(telemetryLogger, data);
  };

  // Resolve Copilot region/fraud restriction for copilot-or-help action routing
  useEffect(() => {
    if (!hasCopilotOrHelpAction || !copilotPermissions || !serverContext) return;
    if (isCopilotFraudRestricted && isCopilotFraudRestricted({ permissions: copilotPermissions, serverContext })) {
      setIsLiveAgentOnlyMode(true);
    } else if (isCopilotRegionRestricted) {
      isCopilotRegionRestricted({
        feature: 'Chat',
        permissions: copilotPermissions,
        serverContext,
      }).then((isRestricted: boolean) => {
        setIsLiveAgentOnlyMode(isRestricted);
      });
    }
  }, [hasCopilotOrHelpAction, copilotPermissions, serverContext]);

  // Log impression on mount and when page changes
  const loggedImpressionRef = useRef<string | null>(null);
  useEffect(() => {
    if (notifications.length <= 0) return;
    const notificationId = notifications[safeIndex]?.notificationId;
    const impressionKey = `${notificationId}-${safeIndex}-${notifications.length}`;
    if (loggedImpressionRef.current === impressionKey) return;
    withTelemetry((logger, data) => {
      logger.logImpression(data, 'TopBanner', safeIndex + 1, notifications.length, renderInstanceIdRef.current);
      loggedImpressionRef.current = impressionKey;
    });
  }, [safeIndex, notifications.length, telemetryLogger]); // eslint-disable-line react-hooks/exhaustive-deps

  if (notifications.length <= 0) {
    return <span />;
  }

  const curMsg = notifications[safeIndex].message;
  const curNotificationId = notifications[safeIndex].notificationId;

  const indexMsg = i18n.getString(
    _TL_('{{page}} of {{total}}'),
    { page: safeIndex + 1, total: notifications.length }
  );

  const canDecrease = safeIndex > 0;
  const decClassNames = `${classes.decButton} ${canDecrease ? classes.error : classes.invalidButton}`;

  const canIncrease = safeIndex + 1 < notifications.length;
  const addClassNames = `${classes.addButton} ${canIncrease ? classes.error : classes.invalidButton}`;

  const logPaginationClick = (direction: string) => {
    withTelemetry((logger, data) => {
      logger.logClick(data, 'TopBanner', ClickType.Pagination, renderInstanceIdRef.current, direction, undefined, undefined, safeIndex + 1, notifications.length);
    });
  };

  const addIndex = () => {
    if (safeIndex + 1 >= notifications.length) {
      return;
    }
    logPaginationClick('next');
    setCurIndex(safeIndex + 1);
  };

  const decIndex = () => {
    if (safeIndex <= 0) {
      return;
    }
    logPaginationClick('previous');
    setCurIndex(safeIndex - 1);
  };

  const actionClickHandler = (action: INotificationAction, actionIndex: number, event: React.MouseEvent) => {
    withTelemetry((logger, data) => {
      logger.logClick(data, 'TopBanner', ClickType.Action, renderInstanceIdRef.current, undefined, undefined, actionIndex, safeIndex + 1, notifications.length);
    });
    if (typeof action.action === 'function') {
      action.action();
      return;
    }
    // Route based on callbackType
    switch (action.callbackType) {
      case 'navigate':
        if (action.href || action.extraAttrs?.href) {
          window.location.href = action.href || action.extraAttrs.href;
        }
        event.preventDefault();
        break;

      case COPILOT_OR_HELP_CALLBACK: {
        // If region/fraud restricted (live-agent-only mode), let the click bubble to the
        // jQuery .HelpTopic delegate handler which opens the help article as fallback.
        if (isLiveAgentOnlyMode) {
          break;
        }
        // Dispatch Copilot toggle event — opens Copilot with grounded AIV guidance.
        // jQuery's $(document).on('click', '.HelpTopic') won't double-fire because
        // the HelpTopic class is filtered out of the button (line ~250) when not in live-agent mode.
        const copilotQuestion = action.extraAttrs?.['data-copilot-question']
          || i18n.getString(_TL_('Why am I seeing an identity verification notice on my account?'));
        document.dispatchEvent(new CustomEvent('copilotToggleEvent', {
          detail: {
            eventType: 'message',
            eventSource: action.extraAttrs?.['data-copilot-source'],
            eventData: {
              message: copilotQuestion,
              context: action.extraAttrs?.['data-copilot-context'],
            },
          },
        }));
        event.preventDefault();
        break;
      }

      default:
        break;
    }
  };

  const dismissHandler = () => {
    withTelemetry((logger, data) => {
      logger.logDismiss(data, 'TopBanner', DismissTrigger.ItemDismiss, renderInstanceIdRef.current, safeIndex + 1, notifications.length);
    });
    const { onDismiss } = notifications[safeIndex];
    if (onDismiss) {
      onDismiss(curNotificationId);
    }
  };

  // Capture clicks on server-rendered <a> links inside Wrapper (dangerouslySetInnerHTML, not covered by actionClickHandler)
  const handleBannerClick = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a') as HTMLAnchorElement | null;
    if (!anchor) return;
    withTelemetry((logger, data) => {
      logger.logClick(data, 'TopBanner', ClickType.Action, renderInstanceIdRef.current, anchor.textContent || undefined, anchor.href || undefined, undefined, safeIndex + 1, notifications.length);
    });
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
    <aside
      className={classNames(
        'notification-center-top-banner',
        classes.topBanner
      )}
      aria-label={i18n.getString(_TL_('Banner message'))}
      onClick={handleBannerClick}
    >
      {notifications.length > 1 ? (
        <div className={classes.combination}>
          <InteractiveGlyphicon
            className={classNames('notification-center-top-banner-dec-button', decClassNames)}
            glyph="ba-ChevronLeftMed"
            onClick={decIndex}
            role="button"
            tabIndex={0}
            aria-label={i18n.getString(_TL_('Previous message'))}
            aria-disabled={!canDecrease}
            onKeyDown={submitEvent(decIndex)}
          />
          <span className={classNames('notification-center-top-banner-index', classes.error)}>{indexMsg}</span>
          <InteractiveGlyphicon
            className={classNames('notification-center-top-banner-add-button', addClassNames)}
            glyph="ba-ChevronRightMed"
            onClick={addIndex}
            role="button"
            tabIndex={0}
            aria-label={i18n.getString(_TL_('Next message'))}
            aria-disabled={!canIncrease}
            onKeyDown={submitEvent(addIndex)}
          />
        </div>
      ) : null}

      {isUXRefreshEnabled ? (
        <DismissCircle20Regular className={classes.errorIcon} />
      ) : (
        <Glyphicon className={classes.errorIcon} glyph="ba-CriticalErrorSolid" />
      )}
      <Wrapper
        message={curMsg}
        notificationId={curNotificationId}
        i18n={i18n}
      />
      {notifications[safeIndex].actionList?.map((action, index) => {
        const ButtonComponent = isUXRefreshEnabled ? FluentButton : Button;
        let extraClassNames = action?.extraClassNames;
        if (extraClassNames && !isLiveAgentOnlyMode && action.callbackType === COPILOT_OR_HELP_CALLBACK && action.extraClassNames?.includes(HELP_TOPIC_CLASS)) {
          extraClassNames = action.extraClassNames.filter((c: string) => c !== HELP_TOPIC_CLASS);
        }
        return action.text && (
          <ButtonComponent
            className={classNames(classes.actionButton, 'notification-center-top-banner-action', extraClassNames)}
            onClick={(e: React.MouseEvent) => actionClickHandler(action, index, e)}
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            {...(isUXRefreshEnabled ? { size: 'small' } : {})}
            {...action.extraAttrs}
          >
            {action.text}
          </ButtonComponent>
        );
      })}
      {notifications[safeIndex].onDismiss && (
        <div
          role="button"
          className={classNames(classes.closeButton, 'notification-center-top-banner-close-button')}
          onClick={dismissHandler}
          aria-label={i18n.getString(_TL_('Dismiss'))}
          tabIndex={0}
          onKeyDown={submitEvent(dismissHandler)}
        >
          <Glyphicon glyph="ba-Cancel" />
        </div>
      )}
    </aside>
  );
}

UnstyledTopBanner.defaultProps = {
  Wrapper: DefaultButtonWrapper,
  telemetryLogger: undefined,
  notificationDataList: undefined,
  serverContext: undefined,
};

// withDefaultStyles doesn't support dynamic styles based on props out of the box
// We need to use a wrapper that passes the correct styles
const StyledTopBannerOld = withDefaultStyles(UnstyledTopBanner, styles);
const StyledTopBannerNew = withDefaultStyles(UnstyledTopBanner, stylesNew);

function TopBannerWrapper(props: Omit<ITopBannerProps, 'classes'>) {
  const {
    isUXRefreshEnabled,
  } = useContext(UXRefreshToggleContext);
  const StyledTopBanner = isUXRefreshEnabled ? StyledTopBannerNew : StyledTopBannerOld;
  return React.createElement(StyledTopBanner, props);
}

export type TopBannerProps = Omit<ITopBannerProps, 'classes' | 'i18n'> & {
  i18n?: NotificationI18n;
};
export const TopBanner = withI18nConsumer(TopBannerWrapper) as React.FC<TopBannerProps>;
export const TopBannerWithStateStore = withStateStore(
  TopBanner,
  notificationStore,
  (store) => {
    const notifications = store.state.bannerNotificationList.map((notification: INotification) => ({
      message: notification.message,
      notificationId: notification.id,
      actionList: notification.actionList,
    }));
    return {
      notifications,
    };
  }
);
